import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CmxDashboardActiveLoadsCardService,
  LoadingStatus
} from '../cmx-dashboard-active-loads-card/cmx-dashboard-active-loads-card.service';
import { Load } from '../cmx-dashboard-active-loads-card/types';
import { CountlyEvents } from '../countly-events.service';

@Component({
  selector: 'cmx-dashboard-track-card',
  templateUrl: './cmx-dashboard-track-card.component.html',
  styleUrls: ['./cmx-dashboard-track-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmxDashboardTrackCardComponent {
  public currentJobsiteId: string;

  @Input()
  public set activeJobsiteId(id: string) {
    this.currentJobsiteId = id;

    this.onTabChanged(true, Tabs.Active);
    this.onTabChanged(false, Tabs.Completed);
    this.onTabChanged(false, Tabs.Planned);
  }

  @Input()
  public set date(newDate: { from: string; to: string }) {
    if (newDate) {
      this.service.fetchNewQueryByDateRange(newDate);

      const now = new Date(new Date().setHours(0, 0, 0, 0)).getTime();

      const dateFrom = new Date(
        new Date(newDate.from).setHours(0, 0, 0, 0)
      ).getTime();
      const dateTo = new Date(
        new Date(newDate.to).setHours(0, 0, 0, 0)
      ).getTime();

      if (dateFrom >= now || dateTo >= now) {
        this.isPlannedDisabled$.next(false);
      } else {
        this.isPlannedDisabled$.next(true);
      }
    } else {
      this.service.fetchNewQueryByDateRange({
        from: new Date(new Date().setHours(0, 0, 0, 0)) as unknown as string,
        to: new Date(new Date().setHours(23, 59)) as unknown as string
      });

      this.isPlannedDisabled$.next(false);
    }

    this.onTabChanged(true, Tabs.Active);
    this.onTabChanged(false, Tabs.Completed);
    this.onTabChanged(false, Tabs.Planned);
  }

  public isPlannedDisabled$ = new BehaviorSubject(false);

  /**
   * @var title Title text
   */
  @Input()
  title: string;
  /**
  /**
   * @var tabsLabels List of tab labels
   */
  @Input()
  tabsLabels: string[] = [];
  /**
   * @var tableColumnsLabels List of table column labels
   */
  @Input()
  tableColumnsLabels: string[] = [];

  /**
   * @var activeLoads list of loads to be shown in the first tab
   */
  @Input()
  activeLoads: Load[] = [];
  /**
   * @var completeLoads list of loads to be shown in the second tab
   */
  @Input()
  completeLoads: Load[] = [];
  /**
   * @var customerId legal entity id to make API requests for
   */
  @Input()
  customerId: number;
  /**
   * @var countryCode country code, used for API requests
   */
  @Input()
  countryCode: string;
  /**
   * @var emptyListText label to show in the empty result message
   */
  @Input()
  emptyListText: string;
  /**
   * @var emptyListDescription description to show in the empty result message
   */
  @Input()
  emptyListDescription: string;

  /**
   * @var productsLabel label for products ending string in products column
   */
  @Input()
  productsLabel: string = 'Products';
  /**
   * @var productsTableLabels labels for products table columns in the overlay
   */
  @Input()
  productsTableLabels: string[] = ['Product', 'Volume'];
  /**
   * @var emptyMessage message shown when no jobsites are available
   */
  @Input()
  emptyMessage: string;
  /**
   * @var extended if to render another two columns
   */
  @Input()
  extended: boolean = false;

  constructor(
    private service: CmxDashboardActiveLoadsCardService,
    private countlyEventsService: CountlyEvents
  ) {}

  public readonly Status = LoadingStatus;
  /**
   * returns async source for status of component
   */
  public get status$() {
    return this.service.status$;
  }

  public goToTrack(load: Load) {
    // @TODO Validate this
    const orderToTrack: OrderToTrack = {
      orderId: `${load.orderId}`,
      orderCode: load.orderCode,
      // Verify
      orderTypeCode: `${load.order.orderTypeId}`,
      //
      businessLineCode: load.order.businessLineCode,
      countryCode: load.order.pointofDelivery.addressItem.countryCode,
      // Verify
      orderHdrId: +load.orderId,
      //
      orderDate: load.order.startOrderDateTime,
      customerId: load.order.customerId
    };

    const encode = encodeURIComponent(JSON.stringify(orderToTrack));
    localStorage.setItem('fromOrders', encode);
    // This is for testing purpose as there are some performance issues DCM021CX0118-30583 Still in investigation
    setTimeout(() => {
      window.location.href = '/track';
    }, 300);
  }

  /**
   * returns async source for table rows
   */
  public get loads$() {
    if (this.activeTab.length > 0) {
      return combineLatest([
        this.service.activeLoads$.pipe(
          map(loads => loads.map(l => ({ ...l, groupStatus: Tabs.Active })))
        ),
        this.service.completeLoads$.pipe(
          map(loads => loads.map(l => ({ ...l, groupStatus: Tabs.Completed })))
        ),
        this.service.plannedLoads$.pipe(
          map(loads => loads.map(l => ({ ...l, groupStatus: Tabs.Planned })))
        )
      ]).pipe(
        map(([activeLoads, completedLoads, plannedLoads]) => {
          return this.activeTab.reduce((loads, activeTab) => {
            if (activeTab === Tabs.Active) {
              return [...loads, ...activeLoads]
                .sort((a, b) => a.jobsiteDesc.localeCompare(b.jobsiteDesc))
                .sort(
                  (a, b) =>
                    new Date(b.deliveryDateTime).getTime() -
                    new Date(a.deliveryDateTime).getTime()
                );
            }
            if (activeTab === Tabs.Completed) {
              return [...loads, ...completedLoads]
                .sort((a, b) => a.jobsiteDesc.localeCompare(b.jobsiteDesc))
                .sort(
                  (a, b) =>
                    new Date(b.deliveryDateTime).getTime() -
                    new Date(a.deliveryDateTime).getTime()
                );
            }
            if (activeTab === Tabs.Planned) {
              return [...loads, ...plannedLoads]
                .sort((a, b) => a.jobsiteDesc.localeCompare(b.jobsiteDesc))
                .sort(
                  (a, b) =>
                    new Date(b.deliveryDateTime).getTime() -
                    new Date(a.deliveryDateTime).getTime()
                );
            }

            return loads;
          }, [] as Load[]);
        })
      );
    }

    throw Error(`Impossible tab name: ${this.activeTab.join()}`);
  }

  /**
   * Gets tab translation
   * @param tab identifier of tab (name)
   * @returns according tab label from tab labels input array
   */
  tabName(tab: Tabs) {
    if (this.tabsLabels && this.tabsLabels.length) {
      if (Tabs.Active === tab) {
        return this.tabsLabels[0];
      } else if (Tabs.Completed === tab) {
        return this.tabsLabels[1];
      } else if (Tabs.Planned === tab) {
        return this.tabsLabels[2];
      }
      throw Error(`Impossible tab name: ${tab}`);
    }
    throw Error(
      'Input property tabsLabels must be set to array of two strings'
    );
  }

  activeTabsKeyValue = { [Tabs.Active]: true };
  activeTab: Tabs[] = [Tabs.Active];
  Tabs = Tabs;

  /**
   * Tab choice handler
   * @param value Tab name
   */
  onTabChanged(value: boolean, key: Tabs) {
    if (this.activeTab.length === 1 && this.activeTab.includes(key) && !value) {
      return;
    }

    this.activeTabsKeyValue = {
      ...this.activeTabsKeyValue,
      [key]: value
    };

    this.activeTab = (Object.keys(this.activeTabsKeyValue) as Tabs[]).filter(
      key => this.activeTabsKeyValue[key]
    );

    this.createCountlyEvent(this.activeTab);
  }

  /**
   * Creating event for analytics
   * @param value Tab name
   */
  createCountlyEvent(tab: Tabs[]) {
    this.countlyEventsService.onChangeForAnalytics(
      'loads_tabs_state',
      tab.join()
    );
  }

  /**
   *
   * @param colIdx Column index
   * @returns column translated label taken from input parameter accordingly
   */
  columnLabel(colIdx: number) {
    if (this.tableColumnsLabels && this.tableColumnsLabels.length > colIdx) {
      return this.tableColumnsLabels[colIdx];
    }
    throw Error(
      `Input property tableColumnsLabels must be set to array of ${
        colIdx + 1
      } strings`
    );
  }
}

enum Tabs {
  Active = 'Active',
  Completed = 'Completed',
  Planned = 'Planned'
}

export interface OrderToTrack {
  orderId: string;
  orderCode: string;
  orderTypeCode: string;
  businessLineCode: string;
  countryCode: string;
  orderHdrId: number;
  orderDate: string;
  customerId: number;
}
